import React, { useState } from 'react';
import { Loader2, Upload, X, Image as ImageIcon, Video } from 'lucide-react';
import { IMAGEKIT_URL_ENDPOINT, IMAGEKIT_PUBLIC_KEY, IMAGEKIT_PRIVATE_KEY } from '../constants';

interface ImageKitUploadProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxFiles?: number;
  accept?: 'images' | 'videos' | 'both';
  maxSizeMB?: number;
}

export const ImageKitUpload: React.FC<ImageKitUploadProps> = ({
  value = [],
  onChange,
  maxFiles = 10,
  accept = 'images',
  maxSizeMB = 1,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const getAcceptString = () => {
    switch (accept) {
      case 'images':
        return 'image/*';
      case 'videos':
        return 'video/*';
      case 'both':
        return 'image/*,video/*';
      default:
        return 'image/*';
    }
  };

  const uploadToImageKit = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);
    formData.append('folder', 'products');
    formData.append('useUniqueFileName', 'true');

    // Get private key from environment (admin-only)
    const privateKey = IMAGEKIT_PRIVATE_KEY;

    console.log('Environment check:', {
      hasPrivateKey: !!privateKey,
      privateKeyPrefix: privateKey?.substring(0, 10) + '...',
      hasUrlEndpoint: !!IMAGEKIT_URL_ENDPOINT,
      hasPublicKey: !!IMAGEKIT_PUBLIC_KEY
    });

    if (!privateKey) {
      throw new Error('ImageKit private key not configured. Please add VITE_IMAGEKIT_PRIVATE_KEY to Vercel environment variables and redeploy.');
    }

    const response = await fetch(`https://upload.imagekit.io/api/v1/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(privateKey + ':')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ImageKit upload error:', errorText);
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result.url;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (value.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const oversizedFiles = files.filter((file: File) => file.size > maxSizeMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Files must be smaller than ${maxSizeMB}MB`);
      return;
    }

    setIsUploading(true);
    const newUrls: string[] = [];

    try {
      for (const file of files as File[]) {
        const fileId = Math.random().toString(36).substring(7);
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

        // Simulate progress (ImageKit doesn't provide progress in basic API)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const current = prev[fileId] || 0;
            if (current < 90) {
              return { ...prev, [fileId]: current + 10 };
            }
            return prev;
          });
        }, 100);

        try {
          const url = await uploadToImageKit(file);
          newUrls.push(url);
          setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        } catch (error) {
          console.error('Upload failed:', error);
          alert(`Failed to upload ${file.name}`);
        } finally {
          clearInterval(progressInterval);
          setTimeout(() => {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
          }, 500);
        }
      }

      onChange([...value, ...newUrls]);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index: number) => {
    const newUrls = value.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  const isVideo = (url: string) => {
    return url.match(/\.(mp4|webm|ogg|mov|avi)$/i);
  };

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="border-2 border-dashed border-orange-200 rounded-2xl p-6 text-center hover:border-orange-400 transition-colors">
        <input
          type="file"
          multiple
          accept={getAcceptString()}
          onChange={handleFileSelect}
          disabled={isUploading || value.length >= maxFiles}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className={`cursor-pointer flex flex-col items-center gap-2 ${
            isUploading || value.length >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isUploading ? (
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          ) : (
            <Upload className="w-8 h-8 text-orange-500" />
          )}
          <span className="text-sm font-medium text-stone-700">
            {isUploading ? 'Uploading...' : `Click to upload ${accept === 'videos' ? 'videos' : accept === 'images' ? 'images' : 'images or videos'}`}
          </span>
          <span className="text-xs text-stone-500">
            Max {maxFiles} files, up to {maxSizeMB}MB each
          </span>
        </label>
      </div>

      {/* Upload Progress */}
      {Object.entries(uploadProgress).map(([fileId, progress]) => (
        <div key={fileId} className="bg-orange-50 rounded-lg p-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-orange-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-stone-600">{progress}%</span>
          </div>
        </div>
      ))}

      {/* File Preview */}
      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {value.map((url, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-xl overflow-hidden border border-orange-200 bg-orange-50">
                {isVideo(url) ? (
                  <video src={url} className="w-full h-full object-cover" />
                ) : (
                  <img src={url} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                )}
              </div>
              <button
                onClick={() => removeFile(index)}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-2 left-2 p-1 bg-black/50 text-white rounded-full">
                {isVideo(url) ? <Video size={12} /> : <ImageIcon size={12} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden inputs for form compatibility */}
      {value.map((url, index) => (
        <input key={index} type="hidden" name={`image_${index}`} value={url} />
      ))}
    </div>
  );
};
